import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import type { ScorecardBrand, ScorecardBrandMapping, ScorecardQuestionRow } from "@/models/Scorecard";
import { availableOptionsFor, type MatchableCategory } from "@/utils/scorecard-utils";
import MappingRow from "./MappingRow";
import UnavailableConfirmDialog from "./UnavailableConfirmDialog";

interface BrandMappingStepProps {
  brands: ScorecardBrand[];
  mappings: ScorecardBrandMapping[];
  responseCategories: MatchableCategory[];
  onManualBind: (brandId: string, responseId: string) => void;
  onMarkUnavailable: (brandId: string, reason: string) => void;
  onRestoreMapping: (brandId: string) => void;
  /** Set when the Scorecard Row Base is an Existing Question Row — e.g.
   *  "Familiarity" — shown as a note under the heading so the researcher
   *  knows that row's own brand mappings are what qualify this row's Base,
   *  even though they aren't repeated here. Also supplies each brand's
   *  status on that row, folded into this row's own chip below so it never
   *  shows "Matched" while the Base is actually unresolved for that brand. */
  existingRowBaseLabel?: string;
  existingBaseRow?: ScorecardQuestionRow;
}

const BrandMappingStep: React.FC<BrandMappingStepProps> = ({
  brands,
  mappings,
  responseCategories,
  onManualBind,
  onMarkUnavailable,
  onRestoreMapping,
  existingRowBaseLabel,
  existingBaseRow,
}) => {
  const mappingByBrandId = new Map(mappings.map((m) => [m.brandId, m]));
  const [pendingUnavailableBrand, setPendingUnavailableBrand] = useState<ScorecardBrand | null>(
    null,
  );
  const [reason, setReason] = useState("");

  const handleConfirmUnavailable = () => {
    if (pendingUnavailableBrand) {
      onMarkUnavailable(pendingUnavailableBrand.id, reason.trim());
    }
    setPendingUnavailableBrand(null);
    setReason("");
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
        Brand Mappings*
      </Typography>
      {existingRowBaseLabel && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mb: 1, display: "block", fontSize: "0.7rem", fontStyle: "italic" }}
        >
          This row's Scorecard Row Base reuses brand mappings from &quot;
          {existingRowBaseLabel}&quot; — resolve issues there instead.
        </Typography>
      )}

      <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
        {brands.map((brand) => (
          <MappingRow
            key={brand.id}
            brand={brand}
            mapping={mappingByBrandId.get(brand.id)}
            availableOptions={availableOptionsFor(brand.id, mappings, responseCategories)}
            onManualBind={onManualBind}
            onMarkUnavailableClick={setPendingUnavailableBrand}
            onRestoreMapping={onRestoreMapping}
            baseStatus={
              existingBaseRow?.brandMappings.find((m) => m.brandId === brand.id)?.status
            }
          />
        ))}
      </Box>

      <UnavailableConfirmDialog
        brandLabel={pendingUnavailableBrand?.label}
        open={!!pendingUnavailableBrand}
        reason={reason}
        onReasonChange={setReason}
        onCancel={() => setPendingUnavailableBrand(null)}
        onConfirm={handleConfirmUnavailable}
      />
    </Box>
  );
};

export default BrandMappingStep;
